import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Squares2X2Icon, RocketLaunchIcon, UsersIcon, LinkIcon, BookOpenIcon, ArrowRightIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { SparklesCore } from '../components/ui/Sparkles';

const services = [
  { titleKey: 'home.cards.store.title', descKey: 'home.cards.store.desc', path: '/store', icon: Squares2X2Icon, color: 'from-primary to-secondary' },
  { titleKey: 'home.cards.smm.title', descKey: 'home.cards.smm.desc', path: '/smm', icon: UsersIcon, color: 'from-primary to-secondary' },
  { titleKey: 'home.cards.proxy.title', descKey: 'home.cards.proxy.desc', path: '/proxy', icon: RocketLaunchIcon, color: 'from-secondary to-secondary-dark' },
  { titleKey: 'home.cards.directory.title', descKey: 'home.cards.directory.desc', path: '/directory', icon: LinkIcon, color: 'from-secondary to-primary' },
  { titleKey: 'home.cards.kiottool.title', descKey: 'home.cards.kiottool.desc', path: '/services/kiottool', icon: WrenchScrewdriverIcon, color: 'from-primary to-secondary' },
  { titleKey: 'home.cards.blog.title', descKey: 'home.cards.blog.desc', path: '/blog', icon: BookOpenIcon, color: 'from-primary to-primary-dark' },
];

const Home = () => {
  const { t, i18n } = useTranslation();
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-dark z-0" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10 z-0 bg-center bg-[length:20px_20px]" />
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute -inset-40 bg-[radial-gradient(ellipse_at_center,rgba(123,44,191,0.10)_0%,rgba(0,0,0,0)_60%)] blur-2xl" />
        </div>
        <div className="w-full absolute inset-0 h-full pointer-events-none hidden md:block">
          <SparklesCore
            id="tsparticlesfullpage"
            background="transparent"
            minSize={0.6}
            maxSize={1.4}
            particleDensity={50}
            className="w-full h-full"
            particleColor="#FFFFFF"
          />
        </div>
        <div className="container mx-auto px-4 z-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            className="text-center max-w-5xl mx-auto relative z-10"
          >
            <h1 className="text-3xl md:text-6xl font-orbitron font-bold mb-6 leading-tight relative z-20">
              <span className="text-white">{t('home.title')}</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed relative z-20">{t('home.subtitle')}</p>
          </motion.div>
          <div className="mt-4 flex justify-center">
            <a
              href="https://kiotso.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost rounded-full px-8 py-3"
            >
              {t('cta.store')}
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 bg-dark-lighter relative overflow-hidden">
        <div className="absolute inset-0 bg-dark opacity-60" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5 z-0 bg-center bg-[length:20px_20px]" />
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute -inset-40 bg-[radial-gradient(ellipse_at_center,rgba(123,44,191,0.06)_0%,rgba(0,0,0,0)_65%)] blur-3xl" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-2xl md:text-3xl font-orbitron font-bold text-center mb-12 text-gray-300">{t('services.highlight')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.3, delay: index * 0.05, ease: 'easeOut' }}
                whileHover={{ y: -4 }}
                className="glass-card glass-card-light neon-hover flex flex-col items-center text-center"
              >
                <service.icon className="w-10 h-10 mb-4 text-primary" />
                <h3 className="text-xl font-bold mb-2">{t(service.titleKey)}</h3>
                <p className="text-gray-400 mb-4 text-sm">{t(service.descKey)}</p>
                <Link
                  to={service.path}
                  className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
                >
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 